/**
 * JSON Parsing and Mapping Service
 *
 * This file handles the complex logic of parsing solution data and mapping
 * fields between the imported solution and the target environment.
 */

import { FIELD_TYPE_MAPPINGS } from "../utils/solutionImportHelpers";

/**
 * Build field mapping from solution components
 * Maps each field type to its occurrences across components
 */
export const buildFieldMapping = (solutionData: any): any => {
  return (
    solutionData.Components?.reduce(
      (acc: any, comp: any) => {
        if (comp.Type === "project") {
          Object.entries(FIELD_TYPE_MAPPINGS || {}).forEach(
            ([rawField, itemField]) => {
              if (rawField === "ApiConnection") {
                Object.keys(comp.Bag?.External?.ApiConnection || {})?.forEach(
                  (apiUUID: string) => {
                    acc[rawField] = {
                      ...acc[rawField],
                      [apiUUID]: [
                        ...(acc[rawField][apiUUID] || []),
                        { comp: comp.ComponentUuid, field: apiUUID },
                      ],
                    };
                  }
                );
              } else if (rawField === "ReportingTemplateField") {
                comp.Item?.ReportingTemplates?.forEach((template: any) =>
                  template.fields?.forEach(({ uuid, order, ...field }: any) => {
                    acc.ReportingTemplateField = {
                      ...acc.ReportingTemplateField,
                      [JSON.stringify(field)]: [
                        ...(acc.ReportingTemplateField[JSON.stringify(field)] ||
                          []),
                        { comp: comp.ComponentUuid, field: uuid },
                      ],
                    };
                  })
                );
              } else if (rawField === "ContactFieldOption") {
                comp.Item?.ContactFields?.forEach(
                  ({ uuid, order, ...template }: any) =>
                    template.options?.forEach(
                      ({ uuid, order, ...field }: any) => {
                        const key = JSON.stringify({
                          field: {
                            ...template,
                            options: template.options?.map(
                              ({ uuid, order, ...f }: any) => ({
                                ...f,
                              })
                            ),
                          },
                          option: field,
                        });
                        acc.ContactFieldOption = {
                          ...acc.ContactFieldOption,
                          [key]: [
                            ...(acc.ContactFieldOption[key] || []),
                            { comp: comp.ComponentUuid, field: uuid },
                          ],
                        };
                      }
                    )
                );
              } else if (rawField === "ReportingTemplate") {
                comp.Item?.ReportingTemplates?.forEach(
                  ({ uuid, order, ...template }: any) => {
                    template.fields = template.fields?.map(
                      ({ uuid, order, ...f }: any) => ({
                        ...f,
                      })
                    );
                    acc[rawField] = {
                      ...acc[rawField],
                      [JSON.stringify(template)]: [
                        ...(acc[rawField][JSON.stringify(template)] || []),
                        { comp: comp.ComponentUuid, field: uuid },
                      ],
                    };
                  }
                );
              } else if (rawField === "ContactField") {
                comp.Item?.ContactFields?.forEach(
                  ({ uuid, order, ...template }: any) => {
                    if (template.options) {
                      template.options = template.options?.map(
                        ({ uuid, order, ...f }: any) => ({
                          ...f,
                        })
                      );
                    }
                    acc[rawField] = {
                      ...acc[rawField],
                      [JSON.stringify(template)]: [
                        ...(acc[rawField][JSON.stringify(template)] || []),
                        { comp: comp.ComponentUuid, field: uuid },
                      ],
                    };
                  }
                );
              } else {
                comp.Item?.[itemField]?.forEach(
                  ({ uuid, order, ...field }: any) => {
                    acc[rawField] = {
                      ...acc[rawField],
                      [JSON.stringify(field)]: [
                        ...(acc[rawField][JSON.stringify(field)] || []),
                        { comp: comp.ComponentUuid, field: uuid },
                      ],
                    };
                  }
                );
              }
            }
          );
        }
        return acc;
      },
      {
        ContactField: {},
        ReportingTemplate: {},
        ApiConnection: {},
        ContactSet: {},
        ReportingTemplateField: {},
        ContactFieldOption: {},
        Topic: {},
      }
    ) || {}
  );
};

/**
 * Create the external fields bag from mapping
 */
export const createExternalFieldsBag = (mapping: any): any => {
  return Object.entries(FIELD_TYPE_MAPPINGS || {}).reduce(
    (acc, [fieldkey, fieldvalue]) => {
      acc[fieldkey] = Object.entries(mapping[fieldkey] || {}).reduce(
        (acc, [key, value]) => {
          acc[key] = "";
          return acc;
        },
        {} as any
      );
      return acc;
    },
    {} as any
  );
};

/**
 * Create the Item structure with field definitions and dependency info
 */
export const createItemStructure = (mapping: any, solutionData: any): any => {
  return Object.entries(FIELD_TYPE_MAPPINGS || {}).reduce(
    (item, [rawField, itemField]) => {
      if (
        rawField === "ReportingTemplateField" ||
        rawField === "ContactFieldOption" ||
        rawField === "ApiConnection"
      )
        return item;

      item[itemField] = Object.entries(mapping[rawField] || {}).map(
        ([key, value]: any) => ({
          ...JSON.parse(key),
          ...(rawField === "ReportingTemplate"
            ? {
                fields:
                  JSON.parse(key).fields?.map(({ uuid, order, ...f }: any) => ({
                    ...f,
                    uuid: JSON.stringify(f),
                  })) || [],
              }
            : {}),
          ...(rawField === "ContactField" && JSON.parse(key).options
            ? {
                options:
                  JSON.parse(key).options?.map(
                    ({ uuid, order, ...f }: any) => ({
                      ...f,
                      uuid: JSON.stringify({
                        field: JSON.parse(key),
                        option: f,
                      }),
                    })
                  ) || [],
              }
            : {}),
          uuid: key,
          info: value.map((v: any) => {
            const info = solutionData.Info.Dependencies.find(
              (d: any) =>
                d.Uuid === v.field && d.SolutionComponentUuid === v.comp
            );
            return {
              SolutionComponentLabel: solutionData.Components?.find(
                (c: any) => c.ComponentUuid === info?.SolutionComponentUuid
              )?.Item?.Label,
              ...info,
            };
          }),
        })
      );
      return item;
    },
    {} as any
  );
};

/**
 * Parse solution data and create the initial imported JSON structure
 */
export const parseSolutionData = (
  solutionData: any
): { mapping: any; importedJSON: any } => {
  const mapping = buildFieldMapping(solutionData);

  const importedJSON = {
    Bag: {
      Internal: {},
      External: createExternalFieldsBag(mapping),
    },
    Item: createItemStructure(mapping, solutionData),
  };

  return { mapping, importedJSON };
};
